import React, { useCallback, useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { cn } from '../cn';
import Button from '../Button';
import PortalCreditLedgerFiltersBar from './PortalCreditLedgerFiltersBar';
import PortalCreditLedgerSummaryGrid from './PortalCreditLedgerSummaryGrid';
import PortalCreditLedgerTable from './PortalCreditLedgerTable';
import PortalCreditLedgerPagination from './PortalCreditLedgerPagination';
import { defaultCreditLedgerFilters } from './ledgerUtils';
export const PortalCreditTransactionsLedger = ({ title = 'Credit transactions', description, loading, data, onFetch, onViewDetail, onExport, exporting, exportDisabled, className, headerExtra, hostSort, sortFetchTick = 0, }) => {
    var _a, _b, _c, _d, _e, _f;
    const [draft, setDraft] = useState(defaultCreditLedgerFilters);
    const [applied, setApplied] = useState(defaultCreditLedgerFilters);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const runFetch = useCallback((nextApplied, nextPage, nextLimit) => {
        void onFetch({ filters: nextApplied, page: nextPage, limit: nextLimit });
    }, [onFetch]);
    const handleApply = () => {
        setApplied(draft);
        setPage(1);
        runFetch(draft, 1, limit);
    };
    const handleSort = (field) => {
        if (hostSort) {
            hostSort.onSort(field);
            return;
        }
        const next = {
            ...applied,
            sort: field,
            sortDir: applied.sort === field && applied.sortDir === 'desc' ? 'asc' : 'desc',
        };
        setApplied(next);
        setDraft((d) => ({ ...d, sort: next.sort, sortDir: next.sortDir }));
        setPage(1);
        runFetch(next, 1, limit);
    };
    const handlePageChange = (p) => {
        setPage(p);
        runFetch(applied, p, limit);
    };
    const handleLimitChange = (l) => {
        setLimit(l);
        setPage(1);
        runFetch(applied, 1, l);
    };
    useEffect(() => {
        runFetch(applied, page, limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
    }, []);
    useEffect(() => {
        if (!hostSort || sortFetchTick === 0)
            return;
        setPage(1);
        runFetch(applied, 1, limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- host-driven refetch
    }, [sortFetchTick]);
    const items = ((_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : []);
    const activeSort = (_b = hostSort === null || hostSort === void 0 ? void 0 : hostSort.sort) !== null && _b !== void 0 ? _b : applied.sort;
    const activeSortDir = (_c = hostSort === null || hostSort === void 0 ? void 0 : hostSort.sortDir) !== null && _c !== void 0 ? _c : applied.sortDir;
    const sortWarning = (_d = data === null || data === void 0 ? void 0 : data.pagination) === null || _d === void 0 ? void 0 : _d.sortWarning;
    const pagination = (_e = data === null || data === void 0 ? void 0 : data.pagination) !== null && _e !== void 0 ? _e : { page: 1, limit: 20, total: 0, totalPages: 0 };
    const isGloballyEmpty = !loading && pagination.total === 0 && !applied.search && applied.typeFilter === 'all';
    return (React.createElement("div", { className: cn('overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm', className) },
        React.createElement("div", { className: "border-b border-gray-100 px-4 py-4 sm:px-6" },
            React.createElement("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" },
                React.createElement("div", null,
                    React.createElement("h1", { className: "text-lg font-bold text-gray-900" }, title),
                    description ? React.createElement("p", { className: "mt-1 text-sm text-gray-500" }, description) : null),
                React.createElement("div", { className: "flex shrink-0 flex-wrap items-center gap-2" },
                    onExport ? (React.createElement(Button, { type: "button", variant: "outline", size: "sm", onClick: () => void onExport({ filters: applied }), disabled: exportDisabled || exporting || loading },
                        React.createElement(Download, { className: cn('mr-2 h-4 w-4', exporting && 'animate-pulse') }),
                        exporting ? 'Exporting…' : 'Export CSV')) : null,
                    headerExtra)),
            React.createElement("div", { className: "mt-4" },
                React.createElement(PortalCreditLedgerSummaryGrid, { summary: (_f = data === null || data === void 0 ? void 0 : data.summary) !== null && _f !== void 0 ? _f : null, loading: loading }))),
        React.createElement(PortalCreditLedgerFiltersBar, { draft: draft, onDraftChange: (patch) => setDraft((prev) => ({ ...prev, ...patch })), onApply: handleApply, applyDisabled: loading }),
        sortWarning ? (React.createElement("p", { className: "border-b border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-900" }, sortWarning)) : null,
        React.createElement(PortalCreditLedgerTable, { rows: items, loading: loading, isGloballyEmpty: isGloballyEmpty, sort: activeSort, sortDir: activeSortDir, onSort: handleSort, onViewDetail: onViewDetail, sortable: Boolean(hostSort) }),
        React.createElement(PortalCreditLedgerPagination, { pagination: pagination, onPageChange: handlePageChange, onLimitChange: handleLimitChange, disabled: loading })));
};
export default PortalCreditTransactionsLedger;
